import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
} from "@mui/material";
import {
  Map as MapIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Gesture as GestureIcon,
  Clear as ClearIcon,
  Done as DoneIcon,
  Restaurant as RestaurantIcon,
  LocationOn as LocationOnIcon,
  PinDrop as PinDropIcon,
  EditLocation as EditLocationIcon,
  Undo as UndoIcon,
  Save as SaveIcon,
  AddLocation as AddLocationIcon,
  RestartAlt as RestartAltIcon,
} from "@mui/icons-material";
import serviceAreasApi, { ServiceArea, DeliveryTimeSlot, InstantServiceArea } from "../../api/serviceAreas";
import kitchensApi from "../../api/kitchens";
import requestedAreasApi from "../../api/requestedAreas";
import targetedExpansionZonesData from "../../constants/targetedExpansionZones.json";
import Layout from "../../components/layout/Layout";
import Swal from "sweetalert2";

interface Kitchen {
  _id: string;
  name: string;
  address: string;
  pin: string;
  isActive: boolean;
  isOpen: boolean;
  kitchenLevel?: number;
  coordinates?: {
    latitude?: number;
    longitude?: number;
  };
}

interface RequestedArea {
  _id: string;
  longitude: number;
  latitude: number;
  address: string;
  userId?: {
    name: string;
    phoneNumber: string;
    email: string;
  };
  createdAt: string;
}

interface TargetedExpansionZone {
  id: string;
  rank: number;
  name: string;
  lat: number;
  lng: number;
  radiusKm: number;
  requestCount: number;
  sharePct?: string;
  polygon?: [number, number][];
}

const DEFAULT_API_KEY = "AIzaSyCwFYHOFKgcgkXqX2z33ryuhgPOYhCNoEY";

// Default time slots to use for new service areas
const DEFAULT_TIME_SLOTS: DeliveryTimeSlot[] = [
  { timeSlot: "05:00 AM - 06:00 AM", isAvailable: true, maxOrdersPerSlot: 100, currentOrders: 0 },
  { timeSlot: "06:00 AM - 07:00 AM", isAvailable: true, maxOrdersPerSlot: 100, currentOrders: 0 },
  { timeSlot: "07:00 AM - 08:00 AM", isAvailable: true, maxOrdersPerSlot: 100, currentOrders: 0 },
  { timeSlot: "08:00 AM - 09:00 AM", isAvailable: true, maxOrdersPerSlot: 100, currentOrders: 0 },
  { timeSlot: "09:00 AM - 10:00 AM", isAvailable: true, maxOrdersPerSlot: 100, currentOrders: 0 },
  { timeSlot: "08:00 PM - 09:00 PM", isAvailable: false, maxOrdersPerSlot: 100, currentOrders: 0 },
];

const ServiceAreasMap: React.FC = () => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);

  // Data State
  const [isLoaded, setIsLoaded] = useState(false);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [instantServiceAreas, setInstantServiceAreas] = useState<InstantServiceArea[]>([]);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [requestedAreas, setRequestedAreas] = useState<RequestedArea[]>([]);
  const [targetedZones] = useState<TargetedExpansionZone[]>(targetedExpansionZonesData as TargetedExpansionZone[]);
  const [requestViewType, setRequestViewType] = useState<"pins" | "heatmap" | "zones">("pins");
  const [zoneShapeType, setZoneShapeType] = useState<"polygons" | "circles">("circles");
  const [demandLimit, setDemandLimit] = useState<number>(1000);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Layers Toggles
  const [showServiceAreas, setShowServiceAreas] = useState(true);
  const [showInstantAreas, setShowInstantAreas] = useState(true);
  const [showKitchens, setShowKitchens] = useState(true);
  const [showRequests, setShowRequests] = useState(true);
  const [showInactiveAreas, setShowInactiveAreas] = useState(true);

  // Sidebar List Tab
  const [sidebarListTab, setSidebarListTab] = useState<"subscription" | "instant" | "zones">("subscription");

  // Drawing State
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawPoints, setDrawPoints] = useState<[number, number][]>([]);

  // Selected Service Area / Edit Dialog State
  const [selectedArea, setSelectedArea] = useState<ServiceArea | null>(null);
  const [selectedInstantArea, setSelectedInstantArea] = useState<InstantServiceArea | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  
  // Form State
  const [areaType, setAreaType] = useState<"subscription" | "instant">("subscription");
  const [formName, setFormName] = useState("");
  const [formPincode, setFormPincode] = useState("");
  const [formIsActive, setFormIsActive] = useState(true);
  const [formEntryPoint, setFormEntryPoint] = useState<[number, number]>([12.9716, 77.5946]);
  const [formCoordinates, setFormCoordinates] = useState<[number, number][]>([]);
  const [formCoordinatesText, setFormCoordinatesText] = useState("");
  const [formTimeSlots, setFormTimeSlots] = useState<DeliveryTimeSlot[]>([]);
  const [formAssociatedKitchenId, setFormAssociatedKitchenId] = useState("");

  // Boundary Edit State (Interactive Map Pin Editing)
  const [boundaryEditMode, setBoundaryEditMode] = useState(false);
  const [editingBoundaryArea, setEditingBoundaryArea] = useState<{
    type: "subscription" | "instant";
    area: ServiceArea | InstantServiceArea;
  } | null>(null);
  const [boundaryPoints, setBoundaryPoints] = useState<[number, number][]>([]);
  const [boundaryHistory, setBoundaryHistory] = useState<[number, number][][]>([]);
  const [addPinMode, setAddPinMode] = useState(false);
  const [isSavingBoundary, setIsSavingBoundary] = useState(false);

  // Refs for cleanup
  const mapClickListener = useRef<google.maps.MapsEventListener | null>(null);
  const drawingPolyRef = useRef<google.maps.Polygon | null>(null);
  const activePolygonsRef = useRef<Record<string, google.maps.Polygon>>({});
  const entryPointMarkersRef = useRef<Record<string, google.maps.Marker>>({});
  const kitchenMarkersRef = useRef<google.maps.Marker[]>([]);
  const requestMarkersRef = useRef<any[]>([]);

  // Boundary editing refs for Google Maps event callbacks
  const editingPolyRef = useRef<google.maps.Polygon | null>(null);
  const boundaryEditModeRef = useRef(false);
  const addPinModeRef = useRef(false);
  const boundaryPointsRef = useRef<[number, number][]>([]);
  const boundaryMapClickListener = useRef<google.maps.MapsEventListener | null>(null);

  useEffect(() => {
    boundaryEditModeRef.current = boundaryEditMode;
  }, [boundaryEditMode]);

  useEffect(() => {
    addPinModeRef.current = addPinMode;
  }, [addPinMode]);

  useEffect(() => {
    boundaryPointsRef.current = boundaryPoints;
  }, [boundaryPoints]);

  // Load API data
  const loadData = useCallback(async () => {
    if (!isLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const [areasRes, instantAreasRes, kitchensRes, requestsRes] = await Promise.all([
        serviceAreasApi.getServiceAreas(),
        serviceAreasApi.getInstantServiceAreas().catch(() => ({ success: true, data: [] })),
        kitchensApi.getAdminKitchens().catch(() => ({ kitchens: [] })),
        requestedAreasApi.getRequestedAreas({ limit: demandLimit }).catch(() => ({ requestedAreas: [] })),
      ]);

      if (areasRes.success) {
        setServiceAreas(areasRes.data);
      } else {
        throw new Error("Failed to load service areas");
      }

      if (instantAreasRes.success) {
        setInstantServiceAreas(instantAreasRes.data);
      } else {
        throw new Error("Failed to load instant service areas");
      }

      setKitchens(kitchensRes.kitchens || []);
      setRequestedAreas(requestsRes.requestedAreas || []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to load visualization data");
    } finally {
      setLoading(false);
    }
  }, [isLoaded, demandLimit]);

  useEffect(() => {
    if (isLoaded) {
      loadData();
    }
  }, [loadData, isLoaded]);

  // Load Google Maps API Script
  useEffect(() => {
    if (!isLoaded) return;
    if (window.google?.maps) {
      setIsMapLoaded(true);
      return;
    }

    const existingScript = document.querySelector(
      `script[src^="https://maps.googleapis.com/maps/api/js"]`
    );
    if (existingScript) {
      existingScript.addEventListener("load", () => setIsMapLoaded(true));
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${DEFAULT_API_KEY}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsMapLoaded(true);
    script.onerror = () => setError("Failed to load Google Maps API script");
    document.head.appendChild(script);
  }, [isLoaded]);

  // Initialize Map
  const initializeMap = useCallback(() => {
    if (!mapContainerRef.current || !isMapLoaded || mapInstance.current) return;

    // Default center to Bangalore center
    const defaultCenter = { lat: 12.9715987, lng: 77.5945627 };

    const mapOptions: google.maps.MapOptions = {
      center: defaultCenter,
      zoom: 12,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    };

    mapInstance.current = new google.maps.Map(
      mapContainerRef.current,
      mapOptions
    );
  }, [isMapLoaded]);

  useEffect(() => {
    initializeMap();
  }, [initializeMap]);

  // Render Polygons and Entry Points
  const renderPolygons = useCallback(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    // Clear existing
    Object.values(activePolygonsRef.current).forEach((p) => p.setMap(null));
    activePolygonsRef.current = {};

    Object.values(entryPointMarkersRef.current).forEach((m) => m.setMap(null));
    entryPointMarkersRef.current = {};

    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    // Draw Subscription Polygons
    if (showServiceAreas) {
      serviceAreas.forEach((area) => {
        if (!area.isActive && !showInactiveAreas) return;

        // If currently editing this subscription area, skip drawing the static polygon (it is rendered as editable polygon)
        if (editingBoundaryArea && editingBoundaryArea.type === "subscription" && editingBoundaryArea.area._id === area._id) {
          return;
        }

        const path = area.coordinates.map((coord) => {
          if (Array.isArray(coord)) {
            return { lat: coord[0], lng: coord[1] };
          }
          return { lat: (coord as any).lat, lng: (coord as any).lng };
        });

        if (path.length === 0) return;

        path.forEach((pt) => {
          bounds.extend(pt);
          hasPoints = true;
        });

        const color = area.isActive ? "#f97316" : "#64748b"; // Orange (active) vs Grey
        const strokeOpacity = boundaryEditMode ? 0.35 : 0.8;
        const fillOpacity = boundaryEditMode ? 0.06 : 0.18;

        const polygon = new google.maps.Polygon({
          paths: path,
          strokeColor: color,
          strokeOpacity: strokeOpacity,
          strokeWeight: 2.5,
          fillColor: color,
          fillOpacity: fillOpacity,
          map: mapInstance.current,
        });

        // Show info window on polygon click
        polygon.addListener("click", (event: google.maps.MapMouseEvent) => {
          const slotsHtml = area.deliveryTimeSlots
            .map(
              (slot) => `
              <div style="display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 2px;">
                <span style="font-weight: 500;">${slot.timeSlot}:</span>
                <span style="color: ${slot.isAvailable ? "#16a34a" : "#dc2626"}; font-weight: bold;">
                  ${slot.isAvailable ? `${slot.currentOrders}/${slot.maxOrdersPerSlot} limit` : "Unavailable"}
                </span>
              </div>`
            )
            .join("");

          const contentString = `
            <div style="padding: 10px; max-width: 260px; font-family: 'Plus Jakarta Sans', sans-serif;">
              <h3 style="margin: 0 0 4px; font-size: 14px; font-weight: bold; color: ${color};">${area.name}</h3>
              <p style="margin: 0 0 8px; font-size: 12px; color: #475569;"><strong>Pincode:</strong> ${area.pincode}</p>
              <div style="border-top: 1px solid #e2e8f0; padding-top: 6px; margin-bottom: 8px;">
                <strong style="font-size: 11px; color: #1e293b; display: block; margin-bottom: 4px;">Time Slots</strong>
                ${slotsHtml}
              </div>
              <div style="display: flex; gap: 6px; margin-top: 6px;">
                <button id="btn-edit-${area._id}" style="background-color: #f97316; color: white; border: none; padding: 5px 9px; border-radius: 4px; font-size: 11px; cursor: pointer; font-weight: 600;">
                  Edit Details
                </button>
                <button id="btn-edit-boundary-${area._id}" style="background-color: #2563eb; color: white; border: none; padding: 5px 9px; border-radius: 4px; font-size: 11px; cursor: pointer; font-weight: 600;">
                  📍 Edit Boundary Pins
                </button>
              </div>
            </div>
          `;

          const infoWindow = new google.maps.InfoWindow({
            content: contentString,
            position: event.latLng,
          });

          infoWindow.open(mapInstance.current);

          // Listen for edit button click inside InfoWindow
          google.maps.event.addListener(infoWindow, "domready", () => {
            const btn = document.getElementById(`btn-edit-${area._id}`);
            if (btn) {
              btn.onclick = () => {
                infoWindow.close();
                handleEditArea(area);
              };
            }
            const boundaryBtn = document.getElementById(`btn-edit-boundary-${area._id}`);
            if (boundaryBtn) {
              boundaryBtn.onclick = () => {
                infoWindow.close();
                startEditingBoundary("subscription", area);
              };
            }
          });
        });

        activePolygonsRef.current[`sub_${area._id}`] = polygon;

        // Draw Entry Point Marker
        if (area.entryPoint && area.entryPoint.length === 2) {
          const marker = new google.maps.Marker({
            position: { lat: area.entryPoint[0], lng: area.entryPoint[1] },
            map: mapInstance.current,
            title: `${area.name} Entry Point`,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: area.isActive ? "#ea580c" : "#64748b",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 1.5,
            },
          });

          marker.addListener("click", () => {
            const infoWindow = new google.maps.InfoWindow({
              content: `<div style="padding: 6px; font-family: sans-serif; font-size: 12px;"><strong>${area.name}</strong> Entry Point<br/>Coords: ${area.entryPoint[0]}, ${area.entryPoint[1]}</div>`,
              position: { lat: area.entryPoint[0], lng: area.entryPoint[1] },
            });
            infoWindow.open(mapInstance.current);
          });

          entryPointMarkersRef.current[area._id!] = marker;
        }
      });
    }

    // Draw Instant Polygons
    if (showInstantAreas) {
      instantServiceAreas.forEach((area) => {
        if (!area.isActive && !showInactiveAreas) return;

        // If currently editing this instant area, skip drawing the static polygon
        if (editingBoundaryArea && editingBoundaryArea.type === "instant" && editingBoundaryArea.area._id === area._id) {
          return;
        }

        const path = area.coordinates.map((coord) => {
          if (Array.isArray(coord)) {
            return { lat: coord[0], lng: coord[1] };
          }
          return { lat: (coord as any).lat, lng: (coord as any).lng };
        });

        if (path.length === 0) return;

        path.forEach((pt) => {
          bounds.extend(pt);
          hasPoints = true;
        });

        const color = area.isActive ? "#14b8a6" : "#64748b"; // Teal (active) vs Grey
        const strokeOpacity = boundaryEditMode ? 0.35 : 0.8;
        const fillOpacity = boundaryEditMode ? 0.06 : 0.18;

        const polygon = new google.maps.Polygon({
          paths: path,
          strokeColor: color,
          strokeOpacity: strokeOpacity,
          strokeWeight: 2.5,
          fillColor: color,
          fillOpacity: fillOpacity,
          map: mapInstance.current,
        });

        // Show info window on polygon click
        polygon.addListener("click", (event: google.maps.MapMouseEvent) => {
          const kitchenName = area.associatedKitchenId?.name || "Unknown Kitchen";
          const contentString = `
            <div style="padding: 10px; max-width: 260px; font-family: 'Plus Jakarta Sans', sans-serif;">
              <h3 style="margin: 0 0 4px; font-size: 14px; font-weight: bold; color: ${color};">${area.name}</h3>
              <p style="margin: 0 0 8px; font-size: 12px; color: #475569;"><strong>Type:</strong> Instant Delivery Area</p>
              <p style="margin: 0 0 8px; font-size: 12px; color: #475569;"><strong>Serving Kitchen:</strong> ${kitchenName}</p>
              <div style="display: flex; gap: 6px; margin-top: 6px;">
                <button id="btn-edit-instant-${area._id}" style="background-color: #14b8a6; color: white; border: none; padding: 5px 9px; border-radius: 4px; font-size: 11px; cursor: pointer; font-weight: 600;">
                  Edit Details
                </button>
                <button id="btn-edit-boundary-instant-${area._id}" style="background-color: #2563eb; color: white; border: none; padding: 5px 9px; border-radius: 4px; font-size: 11px; cursor: pointer; font-weight: 600;">
                  📍 Edit Boundary Pins
                </button>
              </div>
            </div>
          `;

          const infoWindow = new google.maps.InfoWindow({
            content: contentString,
            position: event.latLng,
          });

          infoWindow.open(mapInstance.current);

          // Listen for edit button click inside InfoWindow
          google.maps.event.addListener(infoWindow, "domready", () => {
            const btn = document.getElementById(`btn-edit-instant-${area._id}`);
            if (btn) {
              btn.onclick = () => {
                infoWindow.close();
                handleEditInstantArea(area);
              };
            }
            const boundaryBtn = document.getElementById(`btn-edit-boundary-instant-${area._id}`);
            if (boundaryBtn) {
              boundaryBtn.onclick = () => {
                infoWindow.close();
                startEditingBoundary("instant", area);
              };
            }
          });
        });

        activePolygonsRef.current[`ins_${area._id}`] = polygon;
      });
    }

    // Fit map bounds if loading for first time and points exist
    if (hasPoints && (serviceAreas.length > 0 || instantServiceAreas.length > 0)) {
      mapInstance.current.fitBounds(bounds);
    }
  }, [serviceAreas, instantServiceAreas, showServiceAreas, showInstantAreas, showInactiveAreas, boundaryEditMode, editingBoundaryArea, isMapLoaded]);

  useEffect(() => {
    renderPolygons();
  }, [renderPolygons]);

  // Render Kitchens
  const renderKitchens = useCallback(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    kitchenMarkersRef.current.forEach((m) => m.setMap(null));
    kitchenMarkersRef.current = [];

    if (!showKitchens) return;

    kitchens.forEach((kitchen) => {
      if (
        !kitchen.coordinates ||
        !kitchen.coordinates.latitude ||
        !kitchen.coordinates.longitude
      )
        return;

      const marker = new google.maps.Marker({
        position: {
          lat: kitchen.coordinates.latitude,
          lng: kitchen.coordinates.longitude,
        },
        map: mapInstance.current,
        title: kitchen.name,
        icon: {
          url: "https://maps.google.com/mapfiles/kml/pal2/icon62.png", // Orange/amber kitchen icon
          scaledSize: new google.maps.Size(28, 28),
        },
      });

      const content = `
        <div style="padding: 10px; font-family: 'Plus Jakarta Sans', sans-serif; max-width: 220px;">
          <h4 style="margin: 0 0 4px; font-size: 13px; font-weight: bold; color: #1e293b;">🍳 ${kitchen.name}</h4>
          <p style="margin: 0 0 4px; font-size: 11px; color: #64748b;">${kitchen.address}</p>
          <div style="display: flex; gap: 4px; margin-top: 6px;">
            <span style="background-color: ${kitchen.isOpen ? "#dcfce7" : "#fee2e2"}; color: ${kitchen.isOpen ? "#16a34a" : "#dc2626"}; font-size: 9px; font-weight: bold; padding: 2px 6px; border-radius: 9999px;">
              ${kitchen.isOpen ? "Open" : "Closed"}
            </span>
            <span style="background-color: #f1f5f9; color: #475569; font-size: 9px; font-weight: bold; padding: 2px 6px; border-radius: 9999px;">
              Level ${kitchen.kitchenLevel}
            </span>
          </div>
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({ content });
      marker.addListener("click", () => {
        infoWindow.open(mapInstance.current, marker);
      });

      kitchenMarkersRef.current.push(marker);
    });
  }, [kitchens, showKitchens, isMapLoaded]);

  useEffect(() => {
    renderKitchens();
  }, [renderKitchens]);

  // Render Demand Requests
  const renderRequests = useCallback(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    requestMarkersRef.current.forEach((m) => m.setMap(null));
    requestMarkersRef.current = [];

    if (!showRequests) return;

    if (requestViewType === "pins") {
      requestedAreas.forEach((req) => {
        if (!req.latitude || !req.longitude) return;

        const marker = new google.maps.Marker({
          position: { lat: req.latitude, lng: req.longitude },
          map: mapInstance.current,
          title: req.address || "Demand Request",
          icon: {
            path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z",
            fillColor: "#ef4444", // Crimson Red for requests
            fillOpacity: 0.9,
            strokeWeight: 1,
            strokeColor: "#ffffff",
            scale: 1.1,
            anchor: new google.maps.Point(12, 24),
          },
        });

        const dateStr = new Date(req.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

        const content = `
          <div style="padding: 10px; font-family: 'Plus Jakarta Sans', sans-serif; max-width: 220px;">
            <h4 style="margin: 0 0 4px; font-size: 13px; font-weight: bold; color: #dc2626; display: flex; align-items: center; gap: 4px;">
              <span style="width: 8px; height: 8px; background-color: #ef4444; border-radius: 50%; display: inline-block;"></span>
              Customer Request
            </h4>
            <p style="margin: 0 0 6px; font-size: 11px; color: #1e293b; line-height: 1.4;">${req.address || "No address provided"}</p>
            <div style="border-top: 1px solid #e2e8f0; padding-top: 4px; margin-top: 4px;">
              <p style="margin: 0; font-size: 10px; color: #64748b;"><strong>User:</strong> ${req.userId?.name || "Anonymous"}</p>
              <p style="margin: 2px 0 0; font-size: 10px; color: #64748b;"><strong>Phone:</strong> ${req.userId?.phoneNumber || "N/A"}</p>
              <p style="margin: 2px 0 0; font-size: 10px; color: #94a3b8; text-align: right;">Requested: ${dateStr}</p>
            </div>
          </div>
        `;

        const infoWindow = new google.maps.InfoWindow({ content });
        marker.addListener("click", () => {
          infoWindow.open(mapInstance.current, marker);
        });

        requestMarkersRef.current.push(marker);
      });
    } else if (requestViewType === "heatmap") {
      // Heatmap view (implemented as overlapping semi-transparent circles to avoid deprecated HeatmapLayer)
      requestedAreas.forEach((req) => {
        if (!req.latitude || !req.longitude) return;

        const circle = new google.maps.Circle({
          strokeWeight: 0,
          fillColor: "#ef4444",
          fillOpacity: 0.15,
          map: mapInstance.current,
          center: { lat: req.latitude, lng: req.longitude },
          radius: 150, // 150 meters
          clickable: false,
        });

        requestMarkersRef.current.push(circle);
      });
    } else if (requestViewType === "zones") {
      // Render 15 Targeted Expansion Zones (Polygons or 3.5KM Circles)
      targetedZones.forEach((zone) => {
        let mapShape: google.maps.Polygon | google.maps.Circle;

        if (zoneShapeType === "polygons" && zone.polygon && zone.polygon.length > 0) {
          // Draw Non-Overlapping Polygon Boundary Markup
          const path = zone.polygon.map((coord) => ({ lat: coord[0], lng: coord[1] }));
          mapShape = new google.maps.Polygon({
            paths: path,
            strokeColor: "#7c3aed", // Vibrant Indigo / Purple
            strokeOpacity: 0.9,
            strokeWeight: 2.5,
            fillColor: "#8b5cf6",
            fillOpacity: 0.22,
            map: mapInstance.current,
          });
        } else {
          // Draw Outer Circle Markup
          const outerCircle = new google.maps.Circle({
            strokeColor: "#7c3aed",
            strokeOpacity: 0.85,
            strokeWeight: 2,
            fillColor: "#8b5cf6",
            fillOpacity: 0.14,
            map: mapInstance.current,
            center: { lat: zone.lat, lng: zone.lng },
            radius: (zone.radiusKm || 3.5) * 1000,
          });

          // Draw Inner High-Density Core Disc (Half Radius - Amber/Coral Orange)
          const innerDisc = new google.maps.Circle({
            strokeColor: "#ea580c",
            strokeOpacity: 0.9,
            strokeWeight: 1.5,
            fillColor: "#f97316",
            fillOpacity: 0.35,
            map: mapInstance.current,
            center: { lat: zone.lat, lng: zone.lng },
            radius: ((zone.radiusKm || 3.5) * 1000) / 2, // Half Radius!
          });

          mapShape = outerCircle;
          requestMarkersRef.current.push(innerDisc);
        }

        // Center Pin Marker with Rank Badge
        const marker = new google.maps.Marker({
          position: { lat: zone.lat, lng: zone.lng },
          map: mapInstance.current,
          title: `#${zone.rank} ${zone.name} (${zone.requestCount.toLocaleString("en-IN")} Requests)`,
          label: {
            text: `#${zone.rank}`,
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: "11px",
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 14,
            fillColor: "#7c3aed",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });

        const radiusLabel = `${zone.radiusKm || 3.5}KM Outer / ${(zone.radiusKm || 3.5) / 2}KM Core`;

        const content = `
          <div style="padding: 10px; font-family: 'Plus Jakarta Sans', sans-serif; max-width: 250px;">
            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
              <span style="background-color: #7c3aed; color: #ffffff; font-size: 10px; font-weight: bold; padding: 2px 6px; border-radius: 4px;">
                RANK #${zone.rank}
              </span>
              <span style="background-color: #ffedd5; color: #c2410c; font-size: 10px; font-weight: bold; padding: 2px 6px; border-radius: 4px;">
                ${radiusLabel}
              </span>
            </div>
            <h3 style="margin: 4px 0; font-size: 14px; font-weight: bold; color: #581c87;">${zone.name}</h3>
            <p style="margin: 4px 0; font-size: 12px; color: #475569;">
              <strong>Customer Demand:</strong> <span style="color: #7c3aed; font-weight: bold;">${zone.requestCount.toLocaleString("en-IN")} Requests</span>
            </p>
            <p style="margin: 0 0 8px; font-size: 11px; color: #64748b;">
              <strong>Center Coords:</strong> ${zone.lat}, ${zone.lng}
            </p>
            <div style="border-top: 1px solid #e2e8f0; padding-top: 6px; text-align: right;">
              <span style="font-size: 10px; color: #ea580c; font-weight: 700;">🎯 Core Disc: ${(zone.radiusKm || 3.5) / 2} KM Radius</span>
            </div>
          </div>
        `;

        const infoWindow = new google.maps.InfoWindow({ content });
        marker.addListener("click", () => {
          infoWindow.open(mapInstance.current, marker);
        });
        mapShape.addListener("click", (e: google.maps.MapMouseEvent) => {
          infoWindow.setPosition(e.latLng);
          infoWindow.open(mapInstance.current);
        });

        requestMarkersRef.current.push(mapShape);
        requestMarkersRef.current.push(marker);
      });
    }
  }, [requestedAreas, targetedZones, showRequests, requestViewType, zoneShapeType, isMapLoaded]);

  useEffect(() => {
    renderRequests();
  }, [renderRequests]);

  // Set up Map click listener for Drawing Mode
  useEffect(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    if (drawingMode) {
      if (mapClickListener.current) {
        google.maps.event.removeListener(mapClickListener.current);
      }

      mapInstance.current.setOptions({ draggableCursor: "crosshair" });

      mapClickListener.current = mapInstance.current.addListener(
        "click",
        (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            const newPt: [number, number] = [
              event.latLng.lat(),
              event.latLng.lng(),
            ];
            setDrawPoints((prev) => [...prev, newPt]);
          }
        }
      );
    } else {
      mapInstance.current.setOptions({ draggableCursor: null });
      if (mapClickListener.current) {
        google.maps.event.removeListener(mapClickListener.current);
        mapClickListener.current = null;
      }
    }

    return () => {
      if (mapClickListener.current) {
        google.maps.event.removeListener(mapClickListener.current);
      }
    };
  }, [drawingMode, isMapLoaded]);

  // Update visual polygon of drawn points
  useEffect(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    if (drawingPolyRef.current) {
      drawingPolyRef.current.setMap(null);
      drawingPolyRef.current = null;
    }

    if (drawPoints.length > 0) {
      const path = drawPoints.map((pt) => ({ lat: pt[0], lng: pt[1] }));
      drawingPolyRef.current = new google.maps.Polygon({
        paths: path,
        strokeColor: "#f97316",
        strokeOpacity: 0.85,
        strokeWeight: 3,
        fillColor: "#f97316",
        fillOpacity: 0.3,
        map: mapInstance.current,
      });
    }

    return () => {
      if (drawingPolyRef.current) {
        drawingPolyRef.current.setMap(null);
      }
    };
  }, [drawPoints, isMapLoaded]);

  // Map Actions
  const handleCenterOnArea = (area: ServiceArea) => {
    if (!mapInstance.current) return;
    
    // Zoom and pan
    if (area.entryPoint && area.entryPoint.length === 2) {
      mapInstance.current.setCenter({
        lat: area.entryPoint[0],
        lng: area.entryPoint[1],
      });
      mapInstance.current.setZoom(13.5);
    } else if (area.coordinates.length > 0) {
      const first = area.coordinates[0];
      mapInstance.current.setCenter({
        lat: first[0],
        lng: first[1],
      });
      mapInstance.current.setZoom(13.5);
    }
  };

  const handleCenterOnInstantArea = (area: InstantServiceArea) => {
    if (!mapInstance.current) return;
    
    if (area.coordinates && area.coordinates.length > 0) {
      const first = area.coordinates[0];
      mapInstance.current.setCenter({
        lat: first[0],
        lng: first[1],
      });
      mapInstance.current.setZoom(13.5);
    }
  };

  const handleCenterOnTargetedZone = (zone: TargetedExpansionZone) => {
    if (!mapInstance.current) return;
    mapInstance.current.setCenter({ lat: zone.lat, lng: zone.lng });
    mapInstance.current.setZoom(13);
    if (!showRequests || requestViewType !== "zones") {
      setShowRequests(true);
      setRequestViewType("zones");
    }
  };

  const startDrawing = () => {
    if (boundaryEditMode) {
      setBoundaryEditMode(false);
      setEditingBoundaryArea(null);
      setBoundaryPoints([]);
      setBoundaryHistory([]);
      setAddPinMode(false);
    }
    setDrawPoints([]);
    setDrawingMode(true);
    Swal.fire({
      title: "Drawing Mode Active",
      text: "Click vertices on the map to outline the serviceable boundary. When done, click the 'Finish & Configure' button in the sidebar panel.",
      icon: "info",
      toast: true,
      position: "top-end",
      timer: 5000,
      showConfirmButton: false,
    });
  };

  const cancelDrawing = () => {
    setDrawingMode(false);
    setDrawPoints([]);
  };

  const finishDrawingAndConfigure = () => {
    if (drawPoints.length < 3) {
      Swal.fire("Error", "A polygon must have at least 3 vertices. Please click more points on the map.", "error");
      return;
    }
    
    setDrawingMode(false);
    
    // Auto calculate entryPoint as the center/first point of the drawn points
    const centerLat = drawPoints.reduce((acc, pt) => acc + pt[0], 0) / drawPoints.length;
    const centerLng = drawPoints.reduce((acc, pt) => acc + pt[1], 0) / drawPoints.length;

    // Reset Form for creation
    setSelectedArea(null);
    setSelectedInstantArea(null);
    setAreaType("subscription");
    setFormName("");
    setFormPincode("");
    setFormIsActive(true);
    setFormEntryPoint([centerLat, centerLng]);
    setFormCoordinates(drawPoints);
    setFormCoordinatesText(JSON.stringify(drawPoints));
    setFormTimeSlots(JSON.parse(JSON.stringify(DEFAULT_TIME_SLOTS)));
    setFormAssociatedKitchenId("");

    setIsEditOpen(true);
  };

  // Boundary Editing Handlers & Helpers
  const syncPointsFromPath = useCallback((poly: google.maps.Polygon) => {
    const path = poly.getPath();
    const updated: [number, number][] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const pt = path.getAt(i);
      updated.push([pt.lat(), pt.lng()]);
    }
    setBoundaryPoints(updated);
  }, []);

  const findBestInsertionIndex = (points: [number, number][], newPt: [number, number]): number => {
    if (points.length < 2) return points.length;
    let bestIndex = points.length;
    let minDistance = Infinity;
    const [lat, lng] = newPt;

    for (let i = 0; i < points.length; i++) {
      const nextIdx = (i + 1) % points.length;
      const [lat1, lng1] = points[i];
      const [lat2, lng2] = points[nextIdx];

      const dx = lat2 - lat1;
      const dy = lng2 - lng1;
      const l2 = dx * dx + dy * dy;
      let dist = 0;
      if (l2 === 0) {
        dist = (lat - lat1) * (lat - lat1) + (lng - lng1) * (lng - lng1);
      } else {
        let t = ((lat - lat1) * dx + (lng - lng1) * dy) / l2;
        t = Math.max(0, Math.min(1, t));
        const projX = lat1 + t * dx;
        const projY = lng1 + t * dy;
        dist = (lat - projX) * (lat - projX) + (lng - projY) * (lng - projY);
      }

      if (dist < minDistance) {
        minDistance = dist;
        bestIndex = nextIdx;
      }
    }
    return bestIndex;
  };

  const startEditingBoundary = (type: "subscription" | "instant", area: ServiceArea | InstantServiceArea) => {
    if (drawingMode) {
      cancelDrawing();
    }
    setIsEditOpen(false);

    const initialCoords: [number, number][] = (area.coordinates || []).map((coord) => {
      if (Array.isArray(coord)) {
        return [coord[0], coord[1]];
      }
      return [(coord as any).lat, (coord as any).lng];
    });

    if (initialCoords.length < 3) {
      Swal.fire("Error", "This service area does not have enough coordinates to edit on the map.", "error");
      return;
    }

    setEditingBoundaryArea({ type, area });
    setBoundaryPoints(initialCoords);
    setBoundaryHistory([initialCoords.map((pt) => [pt[0], pt[1]])]);
    setAddPinMode(false);
    setBoundaryEditMode(true);

    // Zoom & pan to the area bounds
    if (mapInstance.current) {
      const bounds = new google.maps.LatLngBounds();
      initialCoords.forEach((pt) => bounds.extend({ lat: pt[0], lng: pt[1] }));
      mapInstance.current.fitBounds(bounds);
    }

    Swal.fire({
      title: `Editing: ${area.name}`,
      text: "Boundary pins are now interactive! Click any pin to delete it, drag pins to reshape, or click 'Add Pins' to extend the area.",
      icon: "info",
      toast: true,
      position: "top-end",
      timer: 5000,
      showConfirmButton: false,
    });
  };

  const handleDeleteVertex = useCallback((vertexIndex: number) => {
    if (!editingPolyRef.current) return;
    const path = editingPolyRef.current.getPath();
    if (path.getLength() <= 3) {
      Swal.fire({
        title: "Minimum 3 Pins Required",
        text: "A service area boundary must have at least 3 pins.",
        icon: "warning",
        toast: true,
        position: "top-end",
        timer: 3000,
        showConfirmButton: false,
      });
      return;
    }

    // Save snapshot before removing
    const currentPts = boundaryPointsRef.current.map((pt) => [pt[0], pt[1]] as [number, number]);
    setBoundaryHistory((prev) => [...prev, currentPts]);

    path.removeAt(vertexIndex);

    Swal.fire({
      title: "Pin Deleted",
      text: `Remaining: ${path.getLength()} pins. Click 'Undo' if you made a mistake.`,
      icon: "info",
      toast: true,
      position: "top-end",
      timer: 2500,
      showConfirmButton: false,
    });
  }, []);

  const handleInsertPin = useCallback((newPt: [number, number]) => {
    if (!editingPolyRef.current) return;
    const path = editingPolyRef.current.getPath();

    // Save snapshot before inserting
    const currentPts = boundaryPointsRef.current.map((pt) => [pt[0], pt[1]] as [number, number]);
    setBoundaryHistory((prev) => [...prev, currentPts]);

    const bestIndex = findBestInsertionIndex(currentPts, newPt);
    path.insertAt(bestIndex, new google.maps.LatLng(newPt[0], newPt[1]));

    Swal.fire({
      title: "Pin Added",
      text: `Total: ${path.getLength()} pins. Drag it to adjust position.`,
      icon: "success",
      toast: true,
      position: "top-end",
      timer: 2500,
      showConfirmButton: false,
    });
  }, []);

  const handleUndoBoundary = () => {
    if (boundaryHistory.length <= 1 || !editingPolyRef.current) return;

    const newHistory = [...boundaryHistory];
    newHistory.pop(); // Remove current state
    const previousPts = newHistory[newHistory.length - 1];
    setBoundaryHistory(newHistory);

    const path = editingPolyRef.current.getPath();
    path.clear();
    previousPts.forEach((pt) => path.push(new google.maps.LatLng(pt[0], pt[1])));
    setBoundaryPoints(previousPts);

    Swal.fire({
      title: "Action Undone",
      text: `Restored to ${previousPts.length} pins.`,
      icon: "info",
      toast: true,
      position: "top-end",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleResetBoundary = () => {
    if (!boundaryHistory.length || !editingPolyRef.current) return;
    const initialPts = boundaryHistory[0];

    const path = editingPolyRef.current.getPath();
    path.clear();
    initialPts.forEach((pt) => path.push(new google.maps.LatLng(pt[0], pt[1])));
    setBoundaryPoints(initialPts);
    setBoundaryHistory([initialPts]);

    Swal.fire({
      title: "Boundary Reset",
      text: `Reset back to original ${initialPts.length} pins.`,
      icon: "info",
      toast: true,
      position: "top-end",
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleCancelBoundaryEdit = async () => {
    if (boundaryHistory.length > 1) {
      const result = await Swal.fire({
        title: "Discard Changes?",
        text: "You have unsaved changes to this boundary. Are you sure you want to discard them?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, discard changes",
      });
      if (!result.isConfirmed) return;
    }

    setBoundaryEditMode(false);
    setEditingBoundaryArea(null);
    setBoundaryPoints([]);
    setBoundaryHistory([]);
    setAddPinMode(false);
  };

  const handleSaveBoundary = async () => {
    if (!editingBoundaryArea) return;

    const path = editingPolyRef.current ? editingPolyRef.current.getPath() : null;
    const latestPoints: [number, number][] = [];
    if (path) {
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i);
        latestPoints.push([pt.lat(), pt.lng()]);
      }
    } else {
      latestPoints.push(...boundaryPoints);
    }

    if (latestPoints.length < 3) {
      Swal.fire("Error", "A serviceable boundary must have at least 3 pins.", "error");
      return;
    }

    setIsSavingBoundary(true);
    try {
      const areaId = editingBoundaryArea.area._id!;
      if (editingBoundaryArea.type === "subscription") {
        const subArea = editingBoundaryArea.area as ServiceArea;
        const payload: Partial<ServiceArea> = {
          name: subArea.name,
          pincode: subArea.pincode,
          isActive: subArea.isActive,
          entryPoint: subArea.entryPoint,
          deliveryTimeSlots: subArea.deliveryTimeSlots,
          coordinates: latestPoints,
        };
        const res = await serviceAreasApi.updateServiceArea(areaId, payload);
        if (res.success) {
          Swal.fire({
            title: "Boundary Saved!",
            text: `Successfully updated boundary for '${editingBoundaryArea.area.name}' (${latestPoints.length} pins saved).`,
            icon: "success",
            timer: 2500,
          });
          setBoundaryEditMode(false);
          setEditingBoundaryArea(null);
          setBoundaryPoints([]);
          setBoundaryHistory([]);
          setAddPinMode(false);
          loadData();
        }
      } else {
        const instantArea = editingBoundaryArea.area as InstantServiceArea;
        const payload: Partial<InstantServiceArea> = {
          name: instantArea.name,
          associatedKitchenId: instantArea.associatedKitchenId?._id || instantArea.associatedKitchenId,
          isActive: instantArea.isActive,
          coordinates: latestPoints,
        };
        const res = await serviceAreasApi.updateInstantServiceArea(areaId, payload);
        if (res.success) {
          Swal.fire({
            title: "Boundary Saved!",
            text: `Successfully updated instant delivery boundary for '${editingBoundaryArea.area.name}' (${latestPoints.length} pins saved).`,
            icon: "success",
            timer: 2500,
          });
          setBoundaryEditMode(false);
          setEditingBoundaryArea(null);
          setBoundaryPoints([]);
          setBoundaryHistory([]);
          setAddPinMode(false);
          loadData();
        }
      }
    } catch (err: any) {
      Swal.fire(
        "Error",
        err.response?.data?.details || err.response?.data?.error || "Failed to save updated boundary coordinates",
        "error"
      );
    } finally {
      setIsSavingBoundary(false);
    }
  };

  // Manage Editable Polygon on Map for Boundary Edit Mode
  useEffect(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    if (editingPolyRef.current) {
      editingPolyRef.current.setMap(null);
      editingPolyRef.current = null;
    }

    if (!boundaryEditMode || !editingBoundaryArea) {
      return;
    }

    const pathCoords = boundaryPoints.map((pt) => ({ lat: pt[0], lng: pt[1] }));
    const color = editingBoundaryArea.type === "subscription" ? "#ea580c" : "#0d9488";

    const poly = new google.maps.Polygon({
      paths: pathCoords,
      strokeColor: color,
      strokeOpacity: 0.95,
      strokeWeight: 3.5,
      fillColor: color,
      fillOpacity: 0.28,
      editable: true,
      draggable: false,
      map: mapInstance.current,
      zIndex: 999,
    });

    editingPolyRef.current = poly;

    const path = poly.getPath();

    const setAtListener = path.addListener("set_at", () => {
      syncPointsFromPath(poly);
    });

    const insertAtListener = path.addListener("insert_at", () => {
      syncPointsFromPath(poly);
    });

    const removeAtListener = path.addListener("remove_at", () => {
      syncPointsFromPath(poly);
    });

    const clickListener = poly.addListener("click", (event: google.maps.PolyMouseEvent) => {
      if (event.vertex !== undefined && event.vertex !== null) {
        handleDeleteVertex(event.vertex);
      } else if (addPinModeRef.current && event.latLng) {
        handleInsertPin([event.latLng.lat(), event.latLng.lng()]);
      }
    });

    const rightClickListener = poly.addListener("rightclick", (event: google.maps.PolyMouseEvent) => {
      if (event.vertex !== undefined && event.vertex !== null) {
        handleDeleteVertex(event.vertex);
      }
    });

    return () => {
      google.maps.event.removeListener(setAtListener);
      google.maps.event.removeListener(insertAtListener);
      google.maps.event.removeListener(removeAtListener);
      google.maps.event.removeListener(clickListener);
      google.maps.event.removeListener(rightClickListener);
      if (editingPolyRef.current) {
        editingPolyRef.current.setMap(null);
        editingPolyRef.current = null;
      }
    };
  }, [boundaryEditMode, editingBoundaryArea?.area._id, isMapLoaded, handleDeleteVertex, handleInsertPin, syncPointsFromPath]);

  // Map Click Listener for "Add Pin Mode"
  useEffect(() => {
    if (!mapInstance.current || !isMapLoaded) return;

    if (boundaryEditMode && addPinMode) {
      mapInstance.current.setOptions({ draggableCursor: "crosshair" });

      boundaryMapClickListener.current = mapInstance.current.addListener(
        "click",
        (event: google.maps.MapMouseEvent) => {
          if (event.latLng) {
            handleInsertPin([event.latLng.lat(), event.latLng.lng()]);
          }
        }
      );
    } else {
      if (!drawingMode) {
        mapInstance.current.setOptions({ draggableCursor: null });
      }
      if (boundaryMapClickListener.current) {
        google.maps.event.removeListener(boundaryMapClickListener.current);
        boundaryMapClickListener.current = null;
      }
    }

    return () => {
      if (boundaryMapClickListener.current) {
        google.maps.event.removeListener(boundaryMapClickListener.current);
        boundaryMapClickListener.current = null;
      }
    };
  }, [boundaryEditMode, addPinMode, drawingMode, isMapLoaded, handleInsertPin]);

  const handleEditArea = (area: ServiceArea) => {
    setSelectedArea(area);
    setSelectedInstantArea(null);
    setAreaType("subscription");
    setFormName(area.name);
    setFormPincode(area.pincode);
    setFormIsActive(area.isActive);
    setFormEntryPoint(area.entryPoint || [12.9716, 77.5946]);
    setFormCoordinates(area.coordinates);
    setFormCoordinatesText(JSON.stringify(area.coordinates));
    
    // Ensure all slots have standard config, or fallback
    const slots = area.deliveryTimeSlots && area.deliveryTimeSlots.length > 0
      ? area.deliveryTimeSlots
      : DEFAULT_TIME_SLOTS;
    setFormTimeSlots(JSON.parse(JSON.stringify(slots)));

    setIsEditOpen(true);
  };

  const handleEditInstantArea = (area: InstantServiceArea) => {
    setSelectedArea(null);
    setSelectedInstantArea(area);
    setAreaType("instant");
    setFormName(area.name);
    setFormPincode("");
    setFormIsActive(area.isActive);
    setFormCoordinates(area.coordinates);
    setFormCoordinatesText(JSON.stringify(area.coordinates));
    setFormAssociatedKitchenId(area.associatedKitchenId?._id || area.associatedKitchenId || "");

    setIsEditOpen(true);
  };

  const handleDeleteInstantArea = async (areaId: string, name: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the instant service area '${name}'? This action is permanent.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    });

    if (result.isConfirmed) {
      try {
        const res = await serviceAreasApi.deleteInstantServiceArea(areaId);
        if (res.success) {
          Swal.fire("Deleted!", "Instant service area has been deleted.", "success");
          loadData();
        }
      } catch (err: any) {
        Swal.fire("Error", err.response?.data?.error || "Failed to delete instant service area", "error");
      }
    }
  };

  const handleDeleteArea = async (areaId: string, name: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the service area '${name}'? This action is permanent.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    });

    if (result.isConfirmed) {
      try {
        const res = await serviceAreasApi.deleteServiceArea(areaId);
        if (res.success) {
          Swal.fire("Deleted!", "Service area has been deleted.", "success");
          loadData();
        }
      } catch (err: any) {
        Swal.fire("Error", err.response?.data?.error || "Failed to delete service area", "error");
      }
    }
  };

  const handleToggleActiveState = async (area: ServiceArea) => {
    try {
      const res = await serviceAreasApi.updateServiceArea(area._id!, {
        isActive: !area.isActive,
      });
      if (res.success) {
        Swal.fire({
          title: "Status Updated",
          text: `'${area.name}' is now ${!area.isActive ? 'Active' : 'Inactive'}`,
          icon: "success",
          toast: true,
          position: "top-end",
          timer: 3000,
          showConfirmButton: false,
        });
        loadData();
      }
    } catch (err: any) {
      Swal.fire("Error", "Failed to toggle status", "error");
    }
  };

  // Form Handlers
  const handleTimeSlotAvailableToggle = (index: number) => {
    const updated = [...formTimeSlots];
    updated[index].isAvailable = !updated[index].isAvailable;
    setFormTimeSlots(updated);
  };

  const handleTimeSlotMaxOrderChange = (index: number, val: number) => {
    const updated = [...formTimeSlots];
    updated[index].maxOrdersPerSlot = val;
    setFormTimeSlots(updated);
  };

  const handleSaveForm = async () => {
    if (!formName.trim()) {
      Swal.fire("Validation Error", "Service Area Name is required.", "error");
      return;
    }
    if (areaType === "subscription" && !/^\d{6}$/.test(formPincode)) {
      Swal.fire("Validation Error", "Pincode must be exactly 6 digits.", "error");
      return;
    }
    if (areaType === "instant" && !formAssociatedKitchenId) {
      Swal.fire("Validation Error", "Please select an associated kitchen.", "error");
      return;
    }

    let coords: [number, number][] = [];
    try {
      coords = JSON.parse(formCoordinatesText);
      if (!Array.isArray(coords) || coords.length < 3) {
        throw new Error("Must be a 2D array with at least 3 points");
      }
      for (const pt of coords) {
        if (!Array.isArray(pt) || pt.length !== 2 || typeof pt[0] !== "number" || typeof pt[1] !== "number") {
          throw new Error("Invalid coordinate values");
        }
      }
    } catch (e) {
      Swal.fire(
        "Validation Error",
        "Invalid coordinates format. Must be a JSON array of coordinate pairs: [[lat, lng], [lat, lng], ...]",
        "error"
      );
      return;
    }

    try {
      if (areaType === "subscription") {
        const payload: Partial<ServiceArea> = {
          name: formName,
          pincode: formPincode,
          isActive: formIsActive,
          entryPoint: formEntryPoint,
          coordinates: coords,
          deliveryTimeSlots: formTimeSlots,
        };

        if (selectedArea) {
          // Update
          const res = await serviceAreasApi.updateServiceArea(selectedArea._id!, payload);
          if (res.success) {
            Swal.fire("Success", "Service area updated successfully!", "success");
            setIsEditOpen(false);
            loadData();
          }
        } else {
          // Create
          const res = await serviceAreasApi.createServiceArea(payload);
          if (res.success) {
            Swal.fire("Success", "New service area created successfully!", "success");
            setIsEditOpen(false);
            setDrawPoints([]);
            loadData();
          }
        }
      } else {
        const payload: Partial<InstantServiceArea> = {
          name: formName,
          coordinates: coords,
          associatedKitchenId: formAssociatedKitchenId,
          isActive: formIsActive,
        };

        if (selectedInstantArea) {
          // Update
          const res = await serviceAreasApi.updateInstantServiceArea(selectedInstantArea._id!, payload);
          if (res.success) {
            Swal.fire("Success", "Instant serviceable area updated successfully!", "success");
            setIsEditOpen(false);
            loadData();
          }
        } else {
          // Create
          const res = await serviceAreasApi.createInstantServiceArea(payload);
          if (res.success) {
            Swal.fire("Success", "New instant serviceable area created successfully!", "success");
            setIsEditOpen(false);
            setDrawPoints([]);
            loadData();
          }
        }
      }
    } catch (err: any) {
      Swal.fire(
        "Error",
        err.response?.data?.error || err.response?.data?.details || "Failed to save service area",
        "error"
      );
    }
  };

  // Stats Counters
  const activeCount = useMemo(() => serviceAreas.filter((a) => a.isActive).length, [serviceAreas]);
  const coveredPincodes = useMemo(() => new Set(serviceAreas.map((a) => a.pincode)).size, [serviceAreas]);

  if (!isLoaded) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 120px)" }}>
          <Paper sx={{ p: 4, textAlign: "center", maxWidth: 500, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.05)", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <MapIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: "secondary.main", mb: 1 }}>
              Serviceable Areas Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
              Visualizing the map triggers calls to the Google Maps JS API and queries operational database layers (service boundaries, active kitchens, and customer expansion requests).
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<MapIcon />}
              onClick={() => setIsLoaded(true)}
              sx={{ px: 4, py: 1.5 }}
            >
              Load Interactive Map
            </Button>
          </Paper>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 90px)" }}>
        {/* Header Panel */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: "secondary.main" }}>
              Serviceable Areas Map
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visualize coverage, kitchens, and local customer demand in a unified GIS map control dashboard.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
            >
              Refresh Data
            </Button>
            {!drawingMode ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startDrawing}
                disabled={loading}
              >
                Draw New Area
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                startIcon={<ClearIcon />}
                onClick={cancelDrawing}
              >
                Cancel Drawing
              </Button>
            )}
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Main Content Workspace */}
        <Box sx={{ display: "flex", flex: 1, gap: 2, minHeight: 0 }}>
          {/* Map View Frame (Left) */}
          <Box sx={{ flex: 1, position: "relative", height: "100%", borderRadius: 3, overflow: "hidden", border: "1px solid #e2e8f0" }}>
            {/* Drawing Notification Overlay */}
            {drawingMode && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10,
                  backgroundColor: "rgba(15, 23, 42, 0.95)",
                  color: "white",
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  border: "1px solid #ea580c",
                  backdropFilter: "blur(4px)",
                }}
              >
                <GestureIcon sx={{ color: "#ea580c" }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Drawing Mode Active ({drawPoints.length} points)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}>
                    Click on the map to place vertices of the delivery boundary.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={finishDrawingAndConfigure}
                  disabled={drawPoints.length < 3}
                  startIcon={<DoneIcon />}
                  sx={{ py: 0.5 }}
                >
                  Finish & Configure
                </Button>
                <IconButton size="small" sx={{ color: "white" }} onClick={cancelDrawing}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* Boundary Edit Mode Floating Toolbar Overlay */}
            {boundaryEditMode && editingBoundaryArea && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10,
                  backgroundColor: "rgba(15, 23, 42, 0.96)",
                  color: "white",
                  px: 2.5,
                  py: 1.2,
                  borderRadius: 2.5,
                  boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  border: "1px solid #3b82f6",
                  backdropFilter: "blur(6px)",
                  maxWidth: "94%",
                  flexWrap: "wrap",
                }}
              >
                <EditLocationIcon sx={{ color: "#3b82f6", fontSize: 26 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#93c5fd" }}>
                    Editing: {editingBoundaryArea.area.name} ({boundaryPoints.length} pins)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block", fontSize: "0.72rem" }}>
                    {addPinMode
                      ? "🎯 Click anywhere on the map to add pins • Drag to move • Click pin to delete"
                      : "👉 Click a pin to delete • Drag pin to move • Drag edge ghost handles or use 'Add Pins' to extend"}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant={addPinMode ? "contained" : "outlined"}
                    color={addPinMode ? "secondary" : "inherit"}
                    onClick={() => setAddPinMode(!addPinMode)}
                    startIcon={<AddLocationIcon />}
                    sx={{
                      py: 0.4,
                      px: 1.2,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      fontWeight: 700,
                      borderColor: "rgba(255,255,255,0.4)",
                      backgroundColor: addPinMode ? "#8b5cf6" : "transparent",
                      color: addPinMode ? "#ffffff" : "#cbd5e1",
                      "&:hover": {
                        backgroundColor: addPinMode ? "#7c3aed" : "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    {addPinMode ? "Adding Pins (Click Map)" : "Add Pins"}
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={handleUndoBoundary}
                    disabled={boundaryHistory.length <= 1}
                    startIcon={<UndoIcon />}
                    sx={{
                      py: 0.4,
                      px: 1,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      borderColor: "rgba(255,255,255,0.3)",
                      color: boundaryHistory.length <= 1 ? "rgba(255,255,255,0.3)" : "#ffffff",
                    }}
                  >
                    Undo
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={handleResetBoundary}
                    startIcon={<RestartAltIcon />}
                    sx={{
                      py: 0.4,
                      px: 1,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      borderColor: "rgba(255,255,255,0.3)",
                      color: "#cbd5e1",
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveBoundary}
                    disabled={boundaryPoints.length < 3 || isSavingBoundary}
                    startIcon={isSavingBoundary ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    sx={{
                      py: 0.4,
                      px: 1.5,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "none",
                      backgroundColor: "#2563eb",
                      "&:hover": { backgroundColor: "#1d4ed8" },
                    }}
                  >
                    Save Boundary
                  </Button>

                  <IconButton size="small" sx={{ color: "white" }} onClick={handleCancelBoundaryEdit}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            )}

            {/* Map Placeholder/Canvas */}
            <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />

            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255,255,255,0.7)",
                  zIndex: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress color="primary" />
              </Box>
            )}
          </Box>

          {/* Controls & List Panel (Right) */}
          <Paper sx={{ width: 340, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", p: 2 }}>
            {/* Quick Metrics */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 2 }}>
              <Card variant="outlined" sx={{ p: 1, textAlign: "center", backgroundColor: "#fffbeb", borderColor: "#fef3c7" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#ea580c" }}>
                  {serviceAreas.length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>
                  Total Areas
                </Typography>
              </Card>
              <Card variant="outlined" sx={{ p: 1, textAlign: "center", backgroundColor: "#f0fdf4", borderColor: "#dcfce7" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#16a34a" }}>
                  {activeCount}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>
                  Active Areas
                </Typography>
              </Card>
              <Card variant="outlined" sx={{ p: 1, textAlign: "center", backgroundColor: "#eff6ff", borderColor: "#dbeafe" }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#1d4ed8" }}>
                  {coveredPincodes}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>
                  Pincodes
                </Typography>
              </Card>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Layer Control Panel */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                <MapIcon fontSize="small" color="action" /> Map Layers Overlay
              </Typography>
              
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showServiceAreas}
                      onChange={(e) => setShowServiceAreas(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                      <span style={{ width: 12, height: 12, backgroundColor: "#f97316", borderRadius: 2, display: "inline-block" }}></span>
                      Subscription Areas
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showInstantAreas}
                      onChange={(e) => setShowInstantAreas(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                      <span style={{ width: 12, height: 12, backgroundColor: "#14b8a6", borderRadius: 2, display: "inline-block" }}></span>
                      Instant Areas
                    </Typography>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showKitchens}
                      onChange={(e) => setShowKitchens(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                      <RestaurantIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
                      Kitchen Locations ({kitchens.length})
                    </Typography>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showRequests}
                      onChange={(e) => setShowRequests(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                      <LocationOnIcon fontSize="small" sx={{ color: "#ef4444", fontSize: 14 }} />
                      Customer Demand ({requestedAreas.length})
                    </Typography>
                  }
                />

                {showRequests && (
                  <Box sx={{ pl: 4, pr: 1, mb: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="caption" color="text.secondary">Visualization:</Typography>
                      <RadioGroup
                        row
                        value={requestViewType}
                        onChange={(e) => setRequestViewType(e.target.value as "pins" | "heatmap" | "zones")}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: 14 } }}
                      >
                        <FormControlLabel 
                          value="pins" 
                          control={<Radio size="small" />} 
                          label={<Typography variant="caption">Pins</Typography>} 
                          sx={{ margin: 0, mr: 0.5 }}
                        />
                        <FormControlLabel 
                          value="heatmap" 
                          control={<Radio size="small" />} 
                          label={<Typography variant="caption">Heatmap</Typography>} 
                          sx={{ margin: 0, mr: 0.5 }}
                        />
                        <FormControlLabel 
                          value="zones" 
                          control={<Radio size="small" />} 
                          label={<Typography variant="caption" sx={{ fontWeight: 700, color: "#7c3aed" }}>3.5KM Zones</Typography>} 
                          sx={{ margin: 0 }}
                        />
                      </RadioGroup>
                    </Box>

                    {requestViewType === "zones" && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 0.75, backgroundColor: "#faf5ff", borderRadius: 1.5, border: "1px solid #e9d5ff" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#6b21a8" }}>Zone Style:</Typography>
                        <RadioGroup
                          row
                          value={zoneShapeType}
                          onChange={(e) => setZoneShapeType(e.target.value as "polygons" | "circles")}
                          sx={{ "& .MuiSvgIcon-root": { fontSize: 13 } }}
                        >
                          <FormControlLabel 
                            value="polygons" 
                            control={<Radio size="small" />} 
                            label={<Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#7c3aed" }}>Polygons</Typography>} 
                            sx={{ margin: 0, mr: 1 }}
                          />
                          <FormControlLabel 
                            value="circles" 
                            control={<Radio size="small" />} 
                            label={<Typography variant="caption" sx={{ fontSize: "0.7rem" }}>Circles</Typography>} 
                            sx={{ margin: 0 }}
                          />
                        </RadioGroup>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography variant="caption" color="text.secondary">Load Limit:</Typography>
                      <Select
                        size="small"
                        value={demandLimit}
                        onChange={(e) => setDemandLimit(Number(e.target.value))}
                        variant="standard"
                        sx={{ fontSize: "0.75rem", minWidth: 80 }}
                      >
                        <MenuItem value={500} sx={{ fontSize: "0.75rem" }}>500</MenuItem>
                        <MenuItem value={1000} sx={{ fontSize: "0.75rem" }}>1000</MenuItem>
                        <MenuItem value={2000} sx={{ fontSize: "0.75rem" }}>2000</MenuItem>
                        <MenuItem value={3000} sx={{ fontSize: "0.75rem" }}>3000</MenuItem>
                        <MenuItem value={5000} sx={{ fontSize: "0.75rem" }}>5000</MenuItem>
                        <MenuItem value={10000} sx={{ fontSize: "0.75rem" }}>10000</MenuItem>
                      </Select>
                    </Box>
                  </Box>
                )}

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showInactiveAreas}
                      onChange={(e) => setShowInactiveAreas(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.825rem" }}>
                      Show Inactive Boundaries
                    </Typography>
                  }
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Area List Selector Tabs */}
            <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
              <Button
                variant={sidebarListTab === "subscription" ? "contained" : "outlined"}
                size="small"
                fullWidth
                onClick={() => setSidebarListTab("subscription")}
                sx={{ fontSize: "0.7rem", px: 0.5, fontWeight: 700, textTransform: "none" }}
              >
                Sub ({serviceAreas.length})
              </Button>
              <Button
                variant={sidebarListTab === "instant" ? "contained" : "outlined"}
                size="small"
                fullWidth
                color="info"
                onClick={() => setSidebarListTab("instant")}
                sx={{ fontSize: "0.7rem", px: 0.5, fontWeight: 700, textTransform: "none" }}
              >
                Instant ({instantServiceAreas.length})
              </Button>
              <Button
                variant={sidebarListTab === "zones" ? "contained" : "outlined"}
                size="small"
                fullWidth
                onClick={() => {
                  setSidebarListTab("zones");
                  if (!showRequests || requestViewType !== "zones") {
                    setShowRequests(true);
                    setRequestViewType("zones");
                  }
                }}
                sx={{
                  fontSize: "0.7rem",
                  px: 0.5,
                  fontWeight: 700,
                  textTransform: "none",
                  backgroundColor: sidebarListTab === "zones" ? "#7c3aed" : "transparent",
                  color: sidebarListTab === "zones" ? "#ffffff" : "#7c3aed",
                  borderColor: "#7c3aed",
                  "&:hover": {
                    backgroundColor: sidebarListTab === "zones" ? "#6d28d9" : "#f3e8ff",
                    borderColor: "#6d28d9",
                  },
                }}
              >
                Targeted Zones ({targetedZones.length})
              </Button>
            </Stack>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {sidebarListTab === "subscription" ? (
                serviceAreas.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No subscription boundaries created yet. Click "Draw New Area" to start mapping.
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {serviceAreas.map((area) => (
                      <ListItem
                        key={area._id}
                        disablePadding
                        secondaryAction={
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Edit Boundary Pins on Map">
                              <IconButton
                                edge="end"
                                size="small"
                                sx={{ color: "#2563eb" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingBoundary("subscription", area);
                                }}
                              >
                                <EditLocationIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              edge="end"
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditArea(area);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteArea(area._id!, area.name);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "#fff7ed",
                          },
                        }}
                      >
                        <ListItemButton 
                          onClick={() => handleCenterOnArea(area)}
                          sx={{ pr: 12, py: 1, borderRadius: 2 }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "secondary.main" }}>
                                {area.name}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  PIN: {area.pincode}
                                </Typography>
                                <Chip
                                  label={area.isActive ? "Active" : "Inactive"}
                                  size="small"
                                  color={area.isActive ? "success" : "default"}
                                  sx={{ height: 16, fontSize: 9, fontWeight: 700 }}
                                />
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )
              ) : sidebarListTab === "instant" ? (
                instantServiceAreas.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No instant boundaries created yet. Click "Draw New Area" to start mapping.
                    </Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {instantServiceAreas.map((area) => (
                      <ListItem
                        key={area._id}
                        disablePadding
                        secondaryAction={
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Edit Boundary Pins on Map">
                              <IconButton
                                edge="end"
                                size="small"
                                sx={{ color: "#2563eb" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingBoundary("instant", area);
                                }}
                              >
                                <EditLocationIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              edge="end"
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditInstantArea(area);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteInstantArea(area._id!, area.name);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            borderColor: "info.main",
                            backgroundColor: "#f0fdfa",
                          },
                        }}
                      >
                        <ListItemButton 
                          onClick={() => handleCenterOnInstantArea(area)}
                          sx={{ pr: 12, py: 1, borderRadius: 2 }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "info.main" }}>
                                {area.name}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Kitchen: {area.associatedKitchenId?.name || "None"}
                                </Typography>
                                <Chip
                                  label={area.isActive ? "Active" : "Inactive"}
                                  size="small"
                                  color={area.isActive ? "success" : "default"}
                                  sx={{ height: 16, fontSize: 9, fontWeight: 700 }}
                                />
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )
              ) : (
                <List dense disablePadding>
                  {targetedZones.map((zone) => (
                    <ListItem
                      key={zone.id}
                      disablePadding
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        border: "1px solid #e9d5ff",
                        backgroundColor: "#faf5ff",
                        "&:hover": {
                          borderColor: "#7c3aed",
                          backgroundColor: "#f3e8ff",
                        },
                      }}
                    >
                      <ListItemButton 
                        onClick={() => handleCenterOnTargetedZone(zone)}
                        sx={{ py: 1, px: 1.5, borderRadius: 2 }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: "#581c87" }}>
                                #{zone.rank} {zone.name}
                              </Typography>
                              <Chip
                                label={`${zone.radiusKm || 3.5}KM Radius`}
                                size="small"
                                sx={{ height: 16, fontSize: 9, fontWeight: 700, backgroundColor: "#7c3aed", color: "#ffffff" }}
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <Typography variant="caption" sx={{ color: "#6b21a8", fontWeight: 600 }}>
                                🎯 {zone.requestCount.toLocaleString("en-IN")} Demand Requests
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#9333ea", fontSize: "0.65rem" }}>
                                View on Map
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Add / Edit Boundary Dialog */}
      <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {selectedArea || selectedInstantArea
            ? `Edit ${areaType === "subscription" ? "Subscription" : "Instant"} Area: ${formName}`
            : "Configure New Service Area"}
        </DialogTitle>
        <DialogContent dividers>
          {!(selectedArea || selectedInstantArea) && (
            <FormControl component="fieldset" sx={{ mb: 2, px: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Area Type Mode</Typography>
              <RadioGroup
                row
                value={areaType}
                onChange={(e) => setAreaType(e.target.value as "subscription" | "instant")}
              >
                <FormControlLabel value="subscription" control={<Radio size="small" />} label="Subscription Area" />
                <FormControlLabel value="instant" control={<Radio size="small" />} label="Instant Delivery Area" />
              </RadioGroup>
            </FormControl>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mt: 1 }}>
            {/* Column 1: Config Details */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Service Area Name"
                fullWidth
                size="small"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. HSR Layout, Indiranagar"
              />

              {areaType === "subscription" ? (
                <>
                  <TextField
                    label="Pincode (6 digits)"
                    fullWidth
                    size="small"
                    value={formPincode}
                    onChange={(e) => setFormPincode(e.target.value)}
                    placeholder="e.g. 560102"
                  />
                  
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="Entry Pt Latitude"
                      size="small"
                      type="number"
                      value={formEntryPoint[0] || ""}
                      onChange={(e) => setFormEntryPoint([parseFloat(e.target.value), formEntryPoint[1]])}
                    />
                    <TextField
                      label="Entry Pt Longitude"
                      size="small"
                      type="number"
                      value={formEntryPoint[1] || ""}
                      onChange={(e) => setFormEntryPoint([formEntryPoint[0], parseFloat(e.target.value)])}
                    />
                  </Box>
                </>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-kitchen-label">Associated Kitchen</InputLabel>
                  <Select
                    labelId="select-kitchen-label"
                    label="Associated Kitchen"
                    value={formAssociatedKitchenId}
                    onChange={(e) => setFormAssociatedKitchenId(e.target.value)}
                  >
                    {kitchens.map((k) => (
                      <MenuItem key={k._id} value={k._id}>
                        {k.name} ({k.isOpen ? "Open" : "Closed"})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={formIsActive}
                    onChange={(e) => setFormIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Service Area Active & Operational"
              />

              {/* Coordinates Output / Input */}
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                    Boundary Vertices (JSON Array format: [[lat, lng], ...])
                  </Typography>
                  {(selectedArea || selectedInstantArea) && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditLocationIcon />}
                      onClick={() => {
                        setIsEditOpen(false);
                        if (selectedArea) {
                          startEditingBoundary("subscription", selectedArea);
                        } else if (selectedInstantArea) {
                          startEditingBoundary("instant", selectedInstantArea);
                        }
                      }}
                      sx={{ fontSize: "0.7rem", py: 0.2, textTransform: "none", fontWeight: 700 }}
                    >
                      Edit Pins on Map
                    </Button>
                  )}
                </Box>
                <TextField
                  multiline
                  rows={6}
                  fullWidth
                  size="small"
                  value={formCoordinatesText}
                  onChange={(e) => setFormCoordinatesText(e.target.value)}
                  InputProps={{ style: { fontFamily: "monospace", fontSize: 11 } }}
                  helperText="Define points by drawing on the map first, or paste JSON coordinate lists here directly."
                />
              </Box>
            </Box>

            {/* Column 2: Time Slots Config or Instant Info Panel */}
            <Box>
              {areaType === "subscription" ? (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                    Configure Delivery Time Slots
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    Configure availability and maximum order limit per slot for this area.
                  </Typography>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Time Slot</TableCell>
                          <TableCell align="center">Active</TableCell>
                          <TableCell align="center">Max Orders</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formTimeSlots.map((slot, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>{slot.timeSlot}</TableCell>
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={slot.isAvailable}
                                onChange={() => handleTimeSlotAvailableToggle(index)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                variant="standard"
                                disabled={!slot.isAvailable}
                                value={slot.maxOrdersPerSlot}
                                onChange={(e) => handleTimeSlotMaxOrderChange(index, parseInt(e.target.value) || 0)}
                                inputProps={{ style: { textAlign: "center", fontSize: 11, width: 60 } }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Paper variant="outlined" sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", backgroundColor: "#f0fdfa", borderColor: "#ccfbf1" }}>
                  <RestaurantIcon sx={{ fontSize: 48, color: "info.main", mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "info.main", mb: 1 }}>
                    Instant Delivery Configuration
                  </Typography>
                  <Typography variant="caption" color="text.secondary" align="center" sx={{ maxWidth: 220, lineHeight: 1.5 }}>
                    Instant delivery operates on-demand. Orders inside this boundary will route directly to the associated kitchen's real-time queue.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveForm} color="primary">
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ServiceAreasMap;
